// Fisier: src/components/SEO.js
// Component pentru meta tags, Open Graph, Twitter Cards și Schema.org JSON-LD

import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({ 
  title, 
  description, 
  slug, 
  path,
  publishDate, 
  type = 'website',
  image,
  author = 'Oana Tenea',
  category,
  tags = [],
  keywords = [],
  faqItems = []
}) => {
  const siteUrl = 'https://oanatenea.ro';
  
  // Determinăm URL-ul canonic corect
  let fullUrl = siteUrl;
  if (slug) {
    fullUrl = `${siteUrl}/blog/${slug}`;
  } else if (path) {
    fullUrl = `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
  } else if (typeof window !== 'undefined' && window.location.pathname !== '/') {
    fullUrl = `${siteUrl}${window.location.pathname}`;
  }

  const fullTitle = title 
    ? (title.includes('Oana Tenea') ? title : `${title} | Oana Tenea - Psihoterapeut`)
    : 'Oana Tenea - Psihoterapeut București & Online | CBT, Schema Therapy, EMDR';

  const defaultDescription = 'Oana Tenea - psihoterapeut cognitiv-comportamental (CBT), specialist în Schema Therapy (ISST), EMDR și terapie de cuplu (EFT). Cabinet în București (Calea Dorobanți) și ședințe online.';
  const metaDescription = description || defaultDescription;
  
  const defaultImage = `${siteUrl}/3DX_2399web.jpg`;
  const imageUrl = image 
    ? (image.startsWith('http') ? image : `${siteUrl}${image.startsWith('/') ? '' : '/'}${image}`) 
    : defaultImage;

  const defaultKeywords = [
    'Oana Tenea',
    'psihoterapeut Oana Tenea',
    'psiholog București',
    'psihoterapie București',
    'terapie cognitiv comportamentală CBT',
    'Schema Therapy București',
    'ISST',
    'terapie de cuplu București',
    'EFT cuplu',
    'EMDR trauma București',
    'tratament anxietate',
    'atacuri de panica',
    'depresie',
    'binge eating',
    'cabinet Dorobanti psiholog',
    'psihoterapie online'
  ];

  const mergedKeywords = Array.from(new Set([...defaultKeywords, ...tags, ...keywords])).join(', ');

  // Schemas
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${siteUrl}/#person`,
    "name": "Oana Tenea",
    "givenName": "Oana",
    "familyName": "Tenea",
    "jobTitle": "Psihoterapeut Cognitiv-Comportamental & Specialist Schema Therapy",
    "description": "Psihoterapeut acreditat de Colegiul Psihologilor din România, specialist Schema Therapy certificat ISST, cu formare în CBT, EMDR și EFT Terapie de Cuplu.",
    "url": siteUrl,
    "image": `${siteUrl}/3DX_2399web.jpg`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Calea Dorobanți 116-122",
      "addressLocality": "București",
      "addressRegion": "București",
      "addressCountry": "RO"
    },
    "memberOf": [
      {
        "@type": "Organization",
        "name": "Colegiul Psihologilor din România (CPR)"
      },
      {
        "@type": "Organization",
        "name": "International Society of Schema Therapy (ISST)",
        "url": "https://www.schematherapysociety.org/"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/psihoterapeut.oanatenea",
      "https://www.facebook.com/PsihologOanaTenea/",
      "https://ro.linkedin.com/in/oana-tenea-7996633",
      "https://www.careshaper.com/p/oana-tenea-9mrpst",
      "https://www.schematherapysociety.org/Sys/PublicProfile/63614207",
      "https://smartliving.ro/author/oana-tenea/"
    ]
  };

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "PsychologicalTreatment", "LocalBusiness"],
    "@id": `${siteUrl}/#practice`,
    "name": "Cabinet Individual de Psihologie Oana Tenea",
    "alternateName": "Oana Tenea - Psihoterapeut",
    "description": "Servicii profesionale de psihoterapie cognitiv-comportamentală (CBT), Schema Therapy, procesare traumă și terapie de cuplu în București (Calea Dorobanți) și online.",
    "url": siteUrl,
    "image": `${siteUrl}/3DX_2399web.jpg`,
    "email": "psihoterapeut@oanatenea.ro",
    "priceRange": "350 - 400 RON",
    "currenciesAccepted": "RON",
    "paymentAccepted": "Cash, Transfer bancar",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Calea Dorobanți 116-122",
      "addressLocality": "București",
      "addressRegion": "Sector 1",
      "postalCode": "010582",
      "addressCountry": "RO"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 44.4532,
      "longitude": 26.0988
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "București"
      },
      {
        "@type": "Country",
        "name": "România"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Online Global"
      }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
        "opens": "09:00",
        "closes": "20:00"
      }
    ],
    "provider": {
      "@id": `${siteUrl}/#person`
    },
    "serviceType": [
      "Psihoterapie individuală",
      "Terapie cognitiv-comportamentală (CBT)",
      "Schema Therapy",
      "Terapie EMDR (procesare traumă)",
      "Terapie de cuplu (EFT)",
      "Consiliere psihologică anxietate și atacuri de panică",
      "Consiliere depresie și tulburări afective",
      "Intervenție mâncat compulsiv (binge eating)",
      "Psihoterapie online"
    ],
    "availableLanguage": ["ro", "en"]
  };

  const articleSchema = type === 'article' ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": metaDescription,
    "url": fullUrl,
    "image": imageUrl,
    "author": {
      "@type": "Person",
      "name": author,
      "url": siteUrl
    },
    "publisher": {
      "@type": "Person",
      "name": "Oana Tenea",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/OanaTenea.png`
      }
    },
    "datePublished": publishDate,
    "dateModified": publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    }
  } : null;

  const faqSchema = (faqItems && faqItems.length > 0) ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question || item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer || item.a
      }
    }))
  } : null;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={mergedKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph meta tags */}
      <meta property="og:site_name" content="Oana Tenea - Psihoterapeut" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="ro_RO" />
      <meta property="og:locale:alternate" content="en_US" />

      {/* Twitter Card meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Article-specific meta tags */}
      {type === 'article' && publishDate && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:published_time" content={publishDate} />
          {category && <meta property="article:section" content={category} />}
          {tags && tags.map((tag, index) => (
            <meta key={`tag-${index}`} property="article:tag" content={String(tag)} />
          ))}
        </>
      )}

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>

      <script type="application/ld+json">
        {JSON.stringify(businessSchema)}
      </script>

      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}

      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
